<template>
  <section>
    <div :class="$style.resourceHeading">
      <HeadingExtraSmall
        data-test-id="UserGroupsList__userGroupsHeading"
        :hasMargin="false"
      >
        {{ $options.userGroupConfig.label }}
      </HeadingExtraSmall>
      <HeadingExtraSmall
        v-if="showResourceCount"
        :class="$style.additionalItems"
        data-test-id="UserGroupsList__userGroupsAdditionalItems"
        :hasMargin="false"
        weight="light"
      >
        {{ getResourceCountText }}
      </HeadingExtraSmall>
    </div>
    <LoadingIcon
      v-if="isLoading"
      :scale="1.75"
    />
    <ResourceError
      v-else-if="hasError"
      data-test-id="UserGroupsList__userGroupsError"
    />
    <ParagraphBody
      v-else-if="showUserGroupsList"
      data-test-id="UserGroupsList__userGroupsList"
    >
      <ButtonLink
        v-for="(group, index) in userGroupsList"
        :key="`group${index}`"
        :class="[$style.linkText, $style.userGroupText]"
        :data-test-id="`UserGroupsList__groupLink_${index}`"
        :text="getUserGroupDisplayText(group.name, index)"
        @click="openUserGroupModal(group.id, group.name)"
      />
    </ParagraphBody>
    <ParagraphBody
      v-else
      data-test-id="UserGroupsList__userGroupsZeroState"
    >
      {{ $options.userGroupConfig.zeroStateText }}
      <ButtonLink
        :class="$style.linkText"
        data-test-id="UserGroupsList__userGroupAssignLink"
        :text="$options.userGroupConfig.linkText"
        @click="navigateToTab('User Groups')"
      />
    </ParagraphBody>
    <UserGroupDetailsModal
      v-if="showResourceGroupModal"
      :class="$style.modal"
      :selectedGroupId="selectedGroupId"
      :selectedGroupName="selectedGroupName"
      :showResourceGroupModal="showResourceGroupModal"
      @close="handleClose"
    />
  </section>
</template>

<script>
import {
  ButtonLink,
  HeadingExtraSmall,
  LoadingIcon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { mapActions } from 'vuex';
import { sortBy } from 'lodash';
import ItemType from 'client/util/Constants/ItemType';
import ResourceError from 'client/systemusers/UserDetails/UserResources/ResourceError.vue';
import UserGroupDetailsModal from 'client/systemusers/UserDetails/UserResources/UserGroupDetailsModal.vue';

const systemUserModel = 'SystemUserModel';
const { UserGroupItemType } = ItemType;
const userGroupItem = new UserGroupItemType();

export default {
  name: 'UserGroupsList',

  components: {
    ButtonLink,
    HeadingExtraSmall,
    LoadingIcon,
    ParagraphBody,
    ResourceError,
    UserGroupDetailsModal,
  },

  data: () => ({
    hasError: false,
    isLoading: true,
    selectedGroupId: '',
    selectedGroupName: '',
    showResourceGroupModal: false,
    totalCount: 0,
    userGroupsList: [],
  }),

  userGroupConfig: {
    label: userGroupItem.titlePlural,
    linkText: `Assign a ${userGroupItem.label}`,
    zeroStateText: `No ${userGroupItem.labelPlural} have been directly assigned to this user.`,
  },

  computed: {
    getResourceCountText() {
      return `Showing first 100 of ${this.totalCount} items`;
    },

    showResourceCount() {
      return this.totalCount > 100;
    },

    showUserGroupsList() {
      return this.userGroupsList.length !== 0;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(systemUserModel, ['fetchUserGroupAssociations']),

    /**
     * Returns the user group name followed by a comma (`,`) unless this is
     * the last (or only) user group in the list.
     */
    getUserGroupDisplayText(name, index) {
      const isLastItemInList = (index === this.userGroupsList.length - 1);

      return isLastItemInList ? name : `${name},`;
    },

    async getUserGroups() {
      const {
        data,
        errorOccurred,
        totalCountOfUserGroups,
      } = await this.fetchUserGroupAssociations();
      const list = [];

      data.forEach((association) => {
        const { id, name } = association;
        list.push({ id, name });
      });

      this.userGroupsList = sortBy(list, 'name');
      this.hasError = errorOccurred;
      this.totalCount = totalCountOfUserGroups;
    },

    handleClose() {
      this.showResourceGroupModal = false;
    },

    async handleMounted() {
      await this.getUserGroups();
      this.isLoading = false;
    },

    navigateToTab(tabLabel) {
      this.$emit('changeTab', tabLabel);
    },

    openUserGroupModal(id, name) {
      this.showResourceGroupModal = true;
      this.selectedGroupId = id;
      this.selectedGroupName = name;
    },
  },
};
</script>

<style scoped module>
.resourceHeading {
  display: flex;
}

.additionalItems {
  padding-left: var(--jc-spacer-small);
}

.linkText.linkText {
  text-transform: none;
}

.userGroupText:not(:first-of-type) {
  margin-left: var(--jc-spacer-x-small);
}

.modal.modal {
  --modal-width: 80ch;
  --modal-max-width: 100%;
}
</style>
